import { Divider, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { GridSortDirection, GridSortModel } from '@mui/x-data-grid';
import Button from 'components/Button';
import Icon from 'components/Icon';
import MenuSectionHeader from 'components/menu/MenuSectionHeader';
import { useIsMobile, useMenu } from 'hooks';
import { BotColumns, BotSortableKeys } from 'pages/projects-page/types';
import { menuMinWidth } from 'theme/utils';
import { Project } from 'types';
import { sortByBoolean, sortByDate } from 'utils';

export const sortByMenuItems: {
  field: BotSortableKeys;
  label: string;
  sorter: (a: Project, b: Project) => number;
}[] = [
  {
    field: BotColumns.Name,
    label: 'Alphabetical',
    sorter: (a, b) => a.name.localeCompare(b.name),
  },
  {
    field: BotColumns.UpdatedAt,
    label: 'Last Modified',
    sorter: (a, b) => sortByDate(a.updatedAt, b.updatedAt),
  },
  {
    field: BotColumns.IsActive,
    label: 'Status',
    sorter: (a, b) =>
      sortByBoolean(
        a.apps.some((app) => app.isActive),
        b.apps.some((app) => app.isActive),
      ),
  },
];

const orderMenuItems: {
  field: GridSortDirection;
  label: { [key in BotSortableKeys]: string };
}[] = [
  {
    field: 'asc',
    label: {
      [BotColumns.Name]: 'A-Z',
      [BotColumns.UpdatedAt]: 'Newest First',
      [BotColumns.IsActive]: 'Active First',
    },
  },
  {
    field: 'desc',
    label: {
      [BotColumns.Name]: 'Z-A',
      [BotColumns.UpdatedAt]: 'Oldest First',
      [BotColumns.IsActive]: 'Inactive First',
    },
  },
];

interface SortByProps {
  sortModel: GridSortModel;
  onSortModelChange: (newSortModel: GridSortModel) => void;
}
const SortByProject = ({ sortModel, onSortModelChange }: SortByProps) => {
  const { anchorEl, isMenuOpen, openMenu, closeMenu } = useMenu();
  const { isMobile } = useIsMobile();

  return (
    <>
      <Button
        onClick={openMenu}
        sx={{
          '& .MuiButton-endIcon': {
            ml: 0.5,
          },
        }}
        size={isMobile ? 'small' : 'medium'}
        endIcon={<Icon icon='expand_more' fontSize='small' />}
        startIcon={
          <Icon
            icon={sortModel[0]?.sort === 'desc' ? 'arrow_downward' : 'arrow_upward'}
            fontSize='small'
          />
        }
      >
        {sortByMenuItems.find((item) => item.field === sortModel[0]?.field)?.label}
      </Button>
      <Menu
        id='demo-positioned-menu'
        aria-labelledby='demo-positioned-button'
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={closeMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: { sx: { minWidth: menuMinWidth } },
        }}
      >
        <MenuSectionHeader title='Sort By' />
        {sortByMenuItems.map((item) => (
          <MenuItem
            key={item.field}
            onClick={() => {
              onSortModelChange(
                sortModel.map((sortItem) => ({
                  field: item.field,
                  sort: sortItem.sort,
                })),
              );
              closeMenu();
            }}
          >
            <ListItemIcon>
              {sortModel.find((sortItem) => sortItem.field === item.field) && (
                <Icon icon='check' fontSize='small' />
              )}
            </ListItemIcon>
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}
        <Divider />
        <MenuSectionHeader title='Order' />
        {orderMenuItems.map((item) => (
          <MenuItem
            key={item.field}
            onClick={() => {
              onSortModelChange(
                sortModel.map((sortItem) => ({
                  field: sortItem.field,
                  sort: item.field,
                })),
              );
              closeMenu();
            }}
          >
            <ListItemIcon>
              {sortModel.find((sortItem) => sortItem.sort === item.field) && (
                <Icon icon='check' fontSize='small' />
              )}
            </ListItemIcon>
            <ListItemText>
              {
                orderMenuItems.find((orderItem) => orderItem.field === item.field)?.label[
                  sortModel[0]?.field as BotSortableKeys
                ]
              }
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default SortByProject;
